import React, { useEffect, useState } from "react";
import styled from "styled-components";
import api from "../../services/api";
import { selectHomeProjects } from "./project-selection";

import ContainerDefault from "../../components/container";

import ProjectCard from "../../components/project-card";

import Button from "../../components/button";

const Container = styled.section`
  padding-top: 56px;

  h2 {
    font: italic bold 26px "Times new roman";
    color: #000000;
    margin-bottom: 35px;
    text-align: center;

    span {
      font: italic bold 26px "Times new roman";
      position: relative;

      &::before {
        content: "";
        border-bottom: 3px solid #f1a857;
        display: block;
        position: absolute;
        bottom: 0;
        left: 0;
        width: 100%;
      }
    }
  }

  .projects-list {
    li {
      margin-bottom: 30px;
    }
  }

  .view-more {
    margin-top: 35px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  @keyframes skeleton-pulse {
    0% {
      background-color: #e8e8e8;
    }
    50% {
      background-color: #d4d4d4;
    }
    100% {
      background-color: #e8e8e8;
    }
  }

  .skeleton-card {
    background: #ffffff;
    border-radius: 3px;
    overflow: hidden;

    .skeleton-image {
      height: 286px;
      width: 100%;
      animation: skeleton-pulse 1.4s ease-in-out infinite;
    }

    .skeleton-info {
      padding: 20px 15px 11px;

      .skeleton-line {
        height: 14px;
        border-radius: 3px;
        animation: skeleton-pulse 1.4s ease-in-out infinite;
        margin-bottom: 10px;

        &.title {
          height: 20px;
          width: 70%;
          margin-bottom: 18px;
        }

        &.short {
          width: 40%;
        }
      }

      .skeleton-footer {
        margin-top: 18px;
        padding-top: 8px;
        border-top: 1px solid #f4f7f6;
        display: flex;
        align-items: center;
        justify-content: space-between;

        .skeleton-avatar {
          display: flex;
          align-items: center;

          .circle {
            width: 30px;
            height: 30px;
            border-radius: 50%;
            animation: skeleton-pulse 1.4s ease-in-out infinite;
          }

          .name {
            width: 80px;
            height: 12px;
            border-radius: 3px;
            margin-left: 10px;
            animation: skeleton-pulse 1.4s ease-in-out infinite;
          }
        }

        .skeleton-likes {
          width: 40px;
          height: 14px;
          border-radius: 3px;
          animation: skeleton-pulse 1.4s ease-in-out infinite;
        }
      }
    }
  }

  @media (min-width: 767px) {
    padding-top: 45px;

    h2 {
      margin-bottom: 45px;
    }

    .projects-list {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      grid-column-gap: 8px;
      grid-row-gap: 24px;

      li {
        margin: 0;

        &:first-child {
          grid-column: 1 / -1;

          .skeleton-image {
            height: 404px;
          }

          .image-container {
            height: 404px;

            .hover-image {
              padding-top: 125px;
            }
          }
          .project-info {
            padding: 16px 35px 21px;

            .project-info-footer {
              .author img {
                width: 46px;
                height: 46px;
              }

              .likes i {
                width: 29px;
                height: 30px;
              }
            }
          }
        }
      }
    }

    .view-more {
      margin-top: 45px;
    }
  }

  @media (min-width: 1200px) {
    padding-top: 45px;

    h2 {
      margin-bottom: 47px;
    }

    .projects-list {
      display: grid;
      grid-template-columns: repeat(6, 1fr);
      grid-column-gap: 15px;
      grid-row-gap: 30px;

      li {
        margin: 0;
        grid-column: auto / span 2;

        &:nth-child(1),
        &:nth-child(2) {
          grid-column: auto / span 3;

          .skeleton-image {
            height: 404px;
          }

          .image-container {
            height: 404px;

            .hover-image {
              padding-top: 125px;
            }
          }
          .project-info {
            padding: 16px 35px 21px;

            .project-info-footer {
              .author img {
                width: 46px;
                height: 46px;
              }

              .likes i {
                width: 29px;
                height: 30px;
              }
            }
          }
        }
      }
    }

    .view-more {
      margin-top: 42px;
    }
  }
`;

export default function HomeProjects() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let isMounted = true;

    async function fetchProjects() {
      try {
        const response = await api.post(
          "https://apimeuambiente.guararapes.com.br/project/get/all/",
          { tipo: "", design: "", tonalidade: "", mdf: "", is_3d: "" }
        );
        if (!Array.isArray(response.data)) {
          throw new Error("Invalid projects response");
        }
        if (isMounted) setProjects(response.data);
      } catch (error) {
        if (isMounted) setError(true);
      } finally {
        if (isMounted) setLoading(false);
      }
    }

    fetchProjects();
    return () => {
      isMounted = false;
    };
  }, []);

  const { featured, latest } = selectHomeProjects(projects);

  return (
    <>
      <ProjectSection
        title={
          <>
            <span>Projetos</span> em destaque
          </>
        }
        projects={featured}
        loading={loading}
        error={error}
      />
      <ProjectSection
        title={
          <>
            <span>Últimos</span> projetos cadastrados
          </>
        }
        projects={latest}
        loading={loading}
        error={error}
      />
    </>
  );
}

function ProjectSection({ title, projects, loading, error }) {
  return (
    <ContainerDefault>
      <Container aria-busy={loading}>
        <h2>{title}</h2>

        {loading ? (
          <ul className="projects-list">
            {Array.from({ length: 5 }).map((_, i) => (
              <li key={i}>
                <div className="skeleton-card">
                  <div className="skeleton-image" />
                  <div className="skeleton-info">
                    <div className="skeleton-line title" />
                    <div className="skeleton-line short" />
                    <div className="skeleton-footer">
                      <div className="skeleton-avatar">
                        <div className="circle" />
                        <div className="name" />
                      </div>
                      <div className="skeleton-likes" />
                    </div>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        ) : error ? (
          <p role="status">
            Não foi possível carregar os projetos. Tente novamente mais tarde.
          </p>
        ) : projects.length > 0 ? (
          <ul className="projects-list">
            {projects.map((project) => (
              <li key={project.id}>
                <ProjectCard
                  id={project.id}
                  image={project.gallery}
                  name={project.name}
                  city={project.city}
                  uf={project.uf}
                  authorImage={
                    project.user && project.user.company_avatar
                      ? project.user.company_avatar
                      : "/images/01.png"
                  }
                  authorName={project.user && project.user.company_name}
                  numberOfLikes={project.likes}
                  products={project.products}
                />
              </li>
            ))}
          </ul>
        ) : (
          <p>Nenhum projeto encontrado.</p>
        )}

        <div className="view-more">
          <Button
            name="Ver todos os projetos"
            to="/ambientes"
            iconName="arrow-right"
          />
        </div>
      </Container>
    </ContainerDefault>
  );
}
